import { Button, Grid, TextField } from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import styles from "./Privacy.module.css";
import { useLoaderContext, useToastContext } from "../../../../Context/ToastContext";
import { useUserContext } from "../../../../Context/UserContext";
import { apiPut } from "../../../../utils/ApiManager";

const changePassword = process.env.REACT_APP_CHANGE_PASSWORD

const Privacy = () => {
    const [privacyData, setPrivacyData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
    });
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const { setLoading } = useLoaderContext();
    const { showToast } = useToastContext();
    const { requestConfirmation, getToken } = useUserContext();

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPrivacyData((prevData) => ({
            ...prevData,
            [name]: value
        }));
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '' // Clear the specific error on change
        }));
    };

    // Validate current password
    const validateCurrentPassword = (password) => {
        return password.trim() === "" ? 'Current password is required' : '';
    };

    // Validate new password
    const validateNewPassword = (password) => {
        if (!password.trim()) return 'New password is required';
        const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
        if (!passwordRegex.test(password)) {
            return 'Password must be at least 8 characters long, contain a capital letter, a number, and a special character';
        }

        // Check if new password is the same as current password
        if (password === privacyData.currentPassword) {
            return 'New password cannot be the same as the current password';
        }

        return '';
    };

    // Validate confirm password
    const validateConfirmPassword = (password) => {
        return password !== privacyData.newPassword ? 'Passwords do not match' : '';
    };

    // Validate the entire form
    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        // Validate current password
        const currentPasswordError = validateCurrentPassword(privacyData.currentPassword);
        if (currentPasswordError) {
            newErrors.currentPassword = currentPasswordError;
            isValid = false;
        }

        // Validate new password
        const newPasswordError = validateNewPassword(privacyData.newPassword);
        if (newPasswordError) {
            newErrors.newPassword = newPasswordError;
            isValid = false;
        }

        // Validate confirm password
        const confirmPasswordError = validateConfirmPassword(privacyData.confirmNewPassword);
        if (confirmPasswordError) {
            newErrors.confirmNewPassword = confirmPasswordError;
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    useEffect(() => {
        const isValid = validateForm();
        setIsButtonDisabled(!isValid); // Enable/disable button based on form validation
    }, [privacyData]);

    // Handle form submit
    const handleUpdate = async () => {
        const result = await requestConfirmation("Want to update password ?");
        if (!result) return;
        try {
            setLoading(true);
            const response = await apiPut(changePassword, privacyData)
            if (response.success) {
                showToast(response?.message || "Privacy settings updated successfully !", "success");
            } else {
                showToast(response?.message || "Failed to update privacy settings !", "error");
            }
        } catch (error) {
            showToast(error?.response?.message || error?.message || "Failed to update privacy settings !", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className={styles.mainProfilePage}>
            <Box className={styles.inputBox} component="form" sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
                <Grid size={{ xs: 12 }} className={styles.gridInput}>
                    <TextField
                        label="Current Password"
                        variant="outlined"
                        fullWidth
                        name="currentPassword"
                        value={privacyData.currentPassword}
                        onChange={handleInputChange}
                        type="password"
                        className={styles.inputTextfield}
                    />
                    {errors.currentPassword && (
                        <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                            {errors.currentPassword}
                        </p>
                    )}
                </Grid>

                <Grid size={{ xs: 12 }} className={styles.gridInput}>
                    <TextField
                        label="New Password"
                        variant="outlined"
                        fullWidth
                        name="newPassword"
                        value={privacyData.newPassword}
                        onChange={handleInputChange}
                        type="password"
                        className={styles.inputTextfield}
                    />
                    {errors.newPassword && (
                        <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                            {errors.newPassword}
                        </p>
                    )}
                </Grid>

                <Grid size={{ xs: 12 }} className={styles.gridInput}>
                    <TextField
                        label="Confirm Password"
                        variant="outlined"
                        fullWidth
                        name="confirmNewPassword"
                        value={privacyData.confirmNewPassword}
                        onChange={handleInputChange}
                        type="password"
                        className={styles.inputTextfield}
                    />
                    {errors.confirmNewPassword && (
                        <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                            {errors.confirmNewPassword}
                        </p>
                    )}
                </Grid>
            </Box>

            <Button
                variant="contained"
                onClick={handleUpdate}
                className={styles.updateButton}
                sx={{ backgroundColor: '#207d46' }}
                disabled={isButtonDisabled} 
            >
                Update
            </Button>
        </Box>
    );
};

export default Privacy;
